import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * Public /privacy — CLAÈ Aesthetics Privacy Policy. Static long-form
 * document rendered in the CLAÈ editorial house style (same spacing,
 * typography and chrome as /terms). Section numbering, headings and
 * body copy are rendered VERBATIM from the clinic-approved text.
 *
 * Rendering choices for a clean, tidy read:
 *   • Section numbers set in uppercase mono-label so they feel like
 *     part of the design system rather than a numbered list.
 *   • Section headings in Cormorant Garamond, matching every other
 *     editorial headline on the site.
 *   • Bullet lists use fine champagne markers rather than default
 *     bullets to stay on-brand.
 *   • Address blocks render with preserved line breaks via <br/>.
 */

const LAST_UPDATED = "4 October 2026";

/** One structured section — ``body`` is a list of blocks. Each block
 * is either a string paragraph or a {list: [...]} shape to render as
 * a bulleted list. Keep it simple — no CMS yet. */
const SECTIONS = [
  {
    n: "1",
    title: "Who we are",
    body: [
      { address: ["CLAÈ Aesthetics", "106 Finchley Road", "South Hampstead", "London", "NW3 5JJ"] },
      { emailLine: "hello@claeaesthetics.co.uk" },
      "CLAÈ Aesthetics is responsible for the personal information collected and processed through our website, booking system and treatment services.",
      "If you have any questions about this Privacy Policy or how we use your information, please contact us at:",
      { emailLine: "hello@claeaesthetics.co.uk" },
    ],
  },
  {
    n: "2",
    title: "Information we collect",
    body: [
      "Depending on how you interact with CLAÈ, we may collect the following information.",
      { subheading: "Personal information" },
      "This may include:",
      {
        list: [
          "Full name",
          "Date of birth",
          "Email address",
          "Telephone number",
          "Address",
          "Client account information",
          "Appointment information",
          "Treatment history",
          "Booking history",
          "Cancellation and rescheduling information",
          "Payment and transaction information",
        ],
      },
      { subheading: "Health and medical information" },
      "When you enquire about or book a treatment, we may collect information necessary to assess your suitability for treatment and provide treatment safely.",
      "This may include:",
      {
        list: [
          "Medical history",
          "Allergies and sensitivities",
          "Current medications",
          "Relevant medical conditions",
          "Previous aesthetic treatments",
          "Previous treatment reactions",
          "Pregnancy or breastfeeding information where relevant",
          "Treatment notes",
          "Consent forms",
          "Consent reconfirmations",
          "Aftercare records",
          "Information relating to treatment outcomes or complications",
        ],
      },
      "Health information is special category personal data under UK data protection law and is subject to additional protection.",
      { subheading: "Clinical photographs and videos" },
      "Where appropriate, we may take clinical photographs or videos as part of your treatment records.",
      "These may be used to:",
      {
        list: [
          "Document your treatment",
          "Monitor treatment progress",
          "Maintain accurate clinical records",
          "Record treatment outcomes",
        ],
      },
      "Clinical photographs and videos will not be used for marketing or social media purposes unless you have given appropriate permission for that separate use.",
    ],
  },
  {
    n: "3",
    title: "How we use your information",
    body: [
      "We may use your information to:",
      {
        list: [
          "Create and manage your client account",
          "Process bookings and appointments",
          "Process payments and deposits",
          "Process refunds where applicable",
          "Send booking confirmations",
          "Send appointment reminders",
          "Send consent forms",
          "Send consent-reconfirmation forms",
          "Assess treatment suitability",
          "Maintain treatment records",
          "Provide treatments safely",
          "Send treatment aftercare information",
          "Manage treatment courses and packages",
          "Manage referral and loyalty credits",
          "Contact you regarding your appointment",
          "Respond to enquiries",
          "Provide customer support",
          "Maintain website security",
          "Prevent fraud or misuse",
          "Improve our services and booking system",
          "Maintain financial and business records",
          "Comply with legal and regulatory obligations",
        ],
      },
      "We aim to collect and retain only information that is relevant and necessary for these purposes.",
    ],
  },
  {
    n: "4",
    title: "Health and medical information",
    body: [
      "Because CLAÈ provides aesthetic treatments, we may need to collect health information before providing certain treatments.",
      "This information allows us to:",
      {
        list: [
          "Assess whether a treatment is suitable",
          "Identify potential contraindications",
          "Identify relevant risks",
          "Make appropriate treatment decisions",
          "Maintain accurate treatment records",
          "Record informed consent",
          "Provide appropriate aftercare",
          "Review changes in medical circumstances before subsequent treatments",
        ],
      },
      "Health information is classified as special category data and requires additional legal protection under the UK GDPR.",
      "CLAÈ will only process health information where an appropriate lawful basis and applicable condition for processing special category data exists.",
    ],
  },
  {
    n: "5",
    title: "Consent forms",
    body: [
      "Certain CLAÈ treatments require you to complete a consent form before treatment.",
      "Your consent form may contain personal information and health information and will be stored with your client and treatment record.",
      "For subsequent treatment sessions, we may ask you to complete a Consent Review and Reconfirmation to confirm whether your medical information has changed.",
      "If you tell us that your medical circumstances have changed, we may need to review the information before your treatment can proceed.",
    ],
  },
  {
    n: "6",
    title: "Clinical photographs and videos",
    body: [
      "Clinical photographs and videos may form part of your confidential treatment record where taken for clinical purposes.",
      "These records will be handled as confidential client information.",
      "We will not use your clinical photographs or videos for advertising, social media, website content or other promotional purposes unless you have provided separate appropriate permission.",
      "Where you provide permission for promotional use, you may withdraw that permission for future use by contacting:",
      { emailLine: "hello@claeaesthetics.co.uk" },
    ],
  },
  {
    n: "7",
    title: "Our lawful bases for processing",
    body: [
      "We will only process personal information where we have an appropriate lawful basis.",
      "Depending on the circumstances, we may rely on:",
      { subheading: "Contract" },
      "Where processing is necessary to provide a service you have requested, such as managing your booking or providing a treatment.",
      { subheading: "Legal obligation" },
      "Where we are required to process information to comply with a legal or regulatory requirement.",
      { subheading: "Legitimate interests" },
      "Where processing is necessary for legitimate business purposes, such as maintaining website security, preventing fraud and managing our business, provided your rights do not override those interests.",
      { subheading: "Consent" },
      "Where you have given consent for a specific purpose, such as certain marketing activities or optional promotional use of photographs.",
      "Where processing is based on consent, you can withdraw your consent at any time.",
      { subheading: "Special category data" },
      "Where we process health information, we will also rely on an appropriate additional condition for processing special category data under applicable UK data protection law.",
    ],
  },
  {
    n: "8",
    title: "Marketing",
    body: [
      "If you choose to receive marketing communications from CLAÈ, we may contact you about:",
      {
        list: [
          "New treatments",
          "Treatment updates",
          "Offers and promotions",
          "Model opportunities",
          "CLAÈ news",
          "Other relevant services",
        ],
      },
      "You can unsubscribe from marketing communications at any time.",
      "Your decision not to receive marketing communications will not affect your ability to book or receive treatments from CLAÈ.",
    ],
  },
  {
    n: "9",
    title: "Payment information",
    body: [
      "Payments made through the CLAÈ booking system may be processed by a third-party payment provider.",
      "CLAÈ may receive information such as:",
      {
        list: [
          "Amount paid",
          "Payment status",
          "Transaction reference",
          "Deposit information",
          "Refund information",
        ],
      },
      "Where payment details are processed directly by a third-party payment provider, CLAÈ does not intentionally store your complete card number or security code.",
    ],
  },
  {
    n: "10",
    title: "Third-party service providers",
    body: [
      "CLAÈ may use trusted third-party providers to operate our website and services.",
      "These may include providers responsible for:",
      {
        list: [
          "Website hosting",
          "Booking and appointment management",
          "Payment processing",
          "Email delivery",
          "Website security",
          "Data storage",
          "IT services",
        ],
      },
      "Where third parties process information on our behalf, we take appropriate steps to ensure that personal information is handled securely and only for authorised purposes.",
    ],
  },
  {
    n: "11",
    title: "Cookies",
    body: [
      "Our website may use cookies and similar technologies.",
      "These may be used to:",
      {
        list: [
          "Keep the website functioning correctly",
          "Enable booking functionality",
          "Remember preferences",
          "Improve website performance",
          "Understand how visitors use the website",
          "Maintain website security",
        ],
      },
      "Where cookies are not strictly necessary for the operation of the website, we will provide appropriate information and obtain consent where required.",
    ],
  },
  {
    n: "12",
    title: "How we protect your information",
    body: [
      "CLAÈ takes reasonable technical and organisational measures to protect personal information from:",
      {
        list: [
          "Unauthorised access",
          "Loss",
          "Misuse",
          "Accidental destruction",
          "Unauthorised alteration",
          "Unauthorised disclosure",
        ],
      },
      "Access to client information will be limited to those who need it for legitimate business or treatment purposes.",
      "Because health information is particularly sensitive, additional care is taken when handling and storing this information.",
      "However, no online system can be guaranteed to be completely secure.",
    ],
  },
  {
    n: "13",
    title: "How long we keep your information",
    body: [
      "We retain personal information only for as long as reasonably necessary for the purposes for which it was collected, including where necessary to:",
      {
        list: [
          "Provide treatment",
          "Maintain client and treatment records",
          "Meet legal or regulatory requirements",
          "Maintain financial records",
          "Resolve disputes",
          "Establish or defend legal claims",
        ],
      },
      "Different types of information may be retained for different periods.",
      "We regularly review the information we hold and will securely delete or anonymise information when it is no longer required.",
    ],
  },
  {
    n: "14",
    title: "Your data protection rights",
    body: [
      "Depending on the circumstances, you may have the right to:",
      {
        list: [
          "Request access to your personal information",
          "Ask us to correct inaccurate information",
          "Ask us to delete information where there is no lawful reason for us to continue holding it",
          "Ask us to restrict processing",
          "Object to certain processing",
          "Request portability of certain information",
          "Withdraw consent where we rely on consent",
          "Complain to the Information Commissioner's Office",
        ],
      },
      "Your rights may be subject to certain legal limitations or exemptions.",
    ],
  },
  {
    n: "15",
    title: "How to exercise your rights",
    body: [
      "If you wish to exercise your data protection rights or have a question about how CLAÈ uses your information, contact:",
      { address: ["CLAÈ Aesthetics", "106 Finchley Road", "South Hampstead", "London", "NW3 5JJ"] },
      { emailLine: "hello@claeaesthetics.co.uk" },
      "We may need to verify your identity before providing access to or changing personal information.",
    ],
  },
  {
    n: "16",
    title: "Data breaches",
    body: [
      "If CLAÈ becomes aware of a personal data breach, we will assess the incident and take appropriate steps in accordance with applicable data protection law.",
      "Where required, we will notify the Information Commissioner's Office and/or affected individuals.",
    ],
  },
  {
    n: "17",
    title: "Third-party websites",
    body: [
      "Our website may contain links to third-party websites, including social media platforms.",
      "CLAÈ is not responsible for the privacy practices or content of third-party websites.",
      "We recommend reviewing the privacy policy of any third-party website you visit.",
    ],
  },
  {
    n: "18",
    title: "Children",
    body: [
      "CLAÈ does not provide aesthetic treatments to anyone under the age of 18.",
      "Our website and booking system may therefore prevent individuals under 18 from progressing with certain bookings.",
      "We do not knowingly collect personal information for the purpose of providing aesthetic treatments to individuals under 18.",
    ],
  },
  {
    n: "19",
    title: "International data transfers",
    body: [
      "Some third-party technology or service providers used by CLAÈ may process personal information outside the UK.",
      "Where personal information is transferred internationally, CLAÈ will take appropriate steps to ensure that the transfer is carried out in accordance with applicable UK data protection law and that appropriate safeguards are in place where required.",
    ],
  },
  {
    n: "20",
    title: "Automated decision-making",
    body: [
      "CLAÈ does not intend to make decisions about your treatment solely through automated decision-making where that decision would have legal or similarly significant effects on you.",
      "Treatment suitability may require review by the practitioner or administration team.",
    ],
  },
  {
    n: "21",
    title: "Changes to this Privacy Policy",
    body: [
      "We may update this Privacy Policy from time to time to reflect:",
      {
        list: [
          "Changes to our services",
          "Changes to our website",
          "Changes to how we process information",
          "Changes to applicable laws or regulations",
        ],
      },
      "The latest version will always be available on our website and will show the date it was last updated.",
      "We will review our privacy information regularly and update it where necessary.",
    ],
  },
  {
    n: "22",
    title: "Complaints",
    body: [
      "If you have concerns about how CLAÈ has handled your personal information, please contact us first:",
      { emailLine: "hello@claeaesthetics.co.uk" },
      "We will try to resolve your concern.",
    ],
  },
];

function Block({ b, idx }) {
  if (typeof b === "string") {
    return (
      <p className="text-[14px] leading-[1.9] text-[#3d3a36]">{b}</p>
    );
  }
  if (b.subheading) {
    return (
      <p className="mono-label text-[11px] tracking-[0.22em] uppercase text-[#141414] mt-1">
        {b.subheading}
      </p>
    );
  }
  if (b.list) {
    return (
      <ul className="pl-0 space-y-[6px]" data-testid={`privacy-list-${idx}`}>
        {b.list.map((item, i) => (
          <li key={i} className="relative pl-5 text-[14px] leading-[1.75] text-[#3d3a36]">
            <span
              aria-hidden
              className="absolute left-0 top-[11px] inline-block h-[1px] w-[10px]"
              style={{ backgroundColor: "#C5A059" }}
            />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (b.address) {
    return (
      <address className="not-italic text-[14px] leading-[1.9] text-[#3d3a36]">
        {b.address.map((line, i) => (
          <span key={i} className="block">{line}</span>
        ))}
      </address>
    );
  }
  if (b.emailLine) {
    return (
      <p className="text-[14px] leading-[1.9]">
        <a
          href={`mailto:${b.emailLine}`}
          className="underline-offset-4 hover:underline"
          style={{ color: "#C5A059" }}
        >
          {b.emailLine}
        </a>
      </p>
    );
  }
  return null;
}

export default function Privacy() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Privacy Policy · CLAÈ";
    return () => { document.title = prev; };
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#FAF9F6", color: "#141414" }}
      data-testid="privacy-page"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <header className="mb-10 sm:mb-14">
          <Link
            to="/"
            className="mono-label text-[10px] tracking-[0.24em] text-[#66615C] hover:text-[#141414]"
            data-testid="privacy-back"
          >
            ← BACK TO CLAÈ
          </Link>
          <p className="mono-label text-[11px] tracking-[0.28em] uppercase text-[#66615C] mt-8">
            CLAÈ AESTHETICS
          </p>
          <h1
            className="font-light tracking-[0.02em] text-[38px] sm:text-[48px] leading-tight mt-2"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="privacy-title"
          >
            Privacy Policy
          </h1>
          <div className="mt-4 h-[2px] w-12" style={{ backgroundColor: "#C5A059" }} />
          <p
            className="mono-label text-[10px] tracking-[0.28em] uppercase text-[#66615C] mt-6"
            data-testid="privacy-last-updated"
          >
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        <div className="space-y-6 text-[14px] leading-[1.9] text-[#3d3a36]" data-testid="privacy-intro">
          <p>
            CLAÈ Aesthetics (“CLAÈ”, “we”, “us” or “our”) respects your privacy and
            is committed to protecting your personal information.
          </p>
          <p>
            This Privacy Policy explains how CLAÈ Aesthetics collects, uses, stores
            and protects your personal information when you visit our website,
            contact us, create an account, book an appointment, purchase a treatment
            or otherwise interact with us.
          </p>
          <p>
            We process personal information in accordance with applicable UK data
            protection legislation, including the UK General Data Protection
            Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>
        </div>

        <div className="mt-12 space-y-10" data-testid="privacy-content">
          {SECTIONS.map((s) => (
            <section key={s.n} data-testid={`privacy-section-${s.n}`}>
              <div className="flex items-baseline gap-4 mb-3">
                <span
                  className="mono-label text-[11px] tracking-[0.28em] uppercase"
                  style={{ color: "#C5A059" }}
                >
                  {s.n.padStart(2, "0")}
                </span>
                <h2
                  className="font-light tracking-[0.01em] text-[22px] sm:text-[26px] leading-snug"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {s.title}
                </h2>
              </div>
              <div className="space-y-4 pl-0 sm:pl-10">
                {s.body.map((b, i) => (
                  <Block key={i} b={b} idx={`${s.n}-${i}`} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-16 pt-8 border-t hairline">
          <p className="text-[11px] text-[#66615C]">
            For questions about your data or this policy, please contact CLAÈ directly at{" "}
            <a
              href="mailto:hello@claeaesthetics.co.uk"
              className="underline-offset-4 hover:underline"
              style={{ color: "#C5A059" }}
            >
              hello@claeaesthetics.co.uk
            </a>
            .
          </p>
        </footer>
      </div>
    </div>
  );
}
